import { useMemo } from "react";
import { useAuth } from "../contexts/AuthContext";
import { UserRole, Permission, Resource, Action } from "../types/user";
import {
  extractUserRole,
  hasPermission,
  canAccessRoute,
  canPerformAction,
  isAdmin,
  isEditor,
  isClient,
  canCreate,
  canRead,
  canUpdate,
  canDelete,
  canManageUsers,
  canAccessSettings,
  canViewAuditLogs,
  canExportData,
  canImportData,
  getAccessibleResources,
  getAvailableActions,
  getRoleRoutes,
} from "../utils/role-utils";

/**
 * Custom hook for role-based access control
 * Updated to work with the new flexible permission system
 */
export const useRoleAccess = () => {
  const { user, isAuthenticated, emailVerified } = useAuth();

  const userRole = useMemo(() => {
    if (!isAuthenticated || !emailVerified) return null;
    return extractUserRole(user);
  }, [user, isAuthenticated, emailVerified]);

  const roleChecks = useMemo(
    () => ({
      isAdmin: isAdmin(userRole),
      isEditor: isEditor(userRole),
      isClient: isClient(userRole),
      hasRole: (role: UserRole) => userRole === role,
      hasAnyRole: (roles: UserRole[]) =>
        userRole ? roles.includes(userRole) : false,
    }),
    [userRole],
  );

  const permissionChecks = useMemo(
    () => ({
      hasPermission: (permission: Permission) =>
        hasPermission(userRole, permission),
      canPerformAction: (resource: Resource, action: Action) =>
        canPerformAction(userRole, resource, action),
      canCreate: (resource: Resource) => canCreate(userRole, resource),
      canRead: (resource: Resource) => canRead(userRole, resource),
      canUpdate: (resource: Resource) => canUpdate(userRole, resource),
      canDelete: (resource: Resource) => canDelete(userRole, resource),
      // Legacy aliases
      canEdit: (resource: Resource) => canUpdate(userRole, resource),
      canView: (resource: Resource) => canRead(userRole, resource),
    }),
    [userRole],
  );

  const specialCapabilities = useMemo(
    () => ({
      canManageUsers: canManageUsers(userRole),
      canAccessSettings: canAccessSettings(userRole),
      canViewAuditLogs: canViewAuditLogs(userRole),
      canExportData: canExportData(userRole),
      canImportData: canImportData(userRole),
    }),
    [userRole],
  );

  const routeChecks = useMemo(
    () => ({
      canAccessRoute: (route: string) => canAccessRoute(userRole, route),
    }),
    [userRole],
  );

  const roleInfo = useMemo(
    () => ({
      userRole,
      accessibleResources: userRole ? getAccessibleResources(userRole) : [],
      availableActions: userRole ? getAvailableActions(userRole) : [],
      accessibleRoutes: userRole ? getRoleRoutes(userRole) : [],
    }),
    [userRole],
  );

  return {
    ...roleInfo,
    ...roleChecks,
    ...permissionChecks,
    ...specialCapabilities,
    ...routeChecks,
  };
};

/**
 * Hook for conditional rendering based on permissions
 */
export const usePermissionGuard = (permission: Permission) => {
  const { hasPermission } = useRoleAccess();
  return hasPermission(permission);
};

/**
 * Hook for conditional rendering based on resource and action
 */
export const useResourceActionGuard = (resource: Resource, action: Action) => {
  const { canPerformAction } = useRoleAccess();
  return canPerformAction(resource, action);
};

/**
 * Hook for conditional rendering based on roles
 */
export const useRoleGuard = (allowedRoles: UserRole | UserRole[]) => {
  const { userRole } = useRoleAccess();

  if (!userRole) return false;

  if (Array.isArray(allowedRoles)) {
    return allowedRoles.includes(userRole);
  }

  return userRole === allowedRoles;
};

/**
 * Hook for route access checking
 */
export const useRouteGuard = (route: string) => {
  const { canAccessRoute } = useRoleAccess();
  return canAccessRoute(route);
};

/**
 * Hook for checking special capabilities
 */
export const useCapabilityGuard = (
  capability:
    | "manageUsers"
    | "accessSettings"
    | "viewAuditLogs"
    | "exportData"
    | "importData",
) => {
  const roleAccess = useRoleAccess();

  switch (capability) {
    case "manageUsers":
      return roleAccess.canManageUsers;
    case "accessSettings":
      return roleAccess.canAccessSettings;
    case "viewAuditLogs":
      return roleAccess.canViewAuditLogs;
    case "exportData":
      return roleAccess.canExportData;
    case "importData":
      return roleAccess.canImportData;
    default:
      return false;
  }
};
